import { useEffect, useRef } from 'react'
import { prefetchModuleForPath } from '@/lib/prefetch/modulePrefetch'
import { prefetchNavigationData } from '@/lib/prefetch/navigationData'
import { getWorkspaceSessionSnapshot } from '@/lib/session/workspace-session'
import { isMainWindow, isPopupWindow, isTauri } from '@/lib/utils/tauri'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'

/** Warm at most two previously visited routes, after interaction has settled. */
export function useTauriModulePrefetch(): void {
  const { pathname } = useLocation()
  const client = useQueryClient()
  const recent = useRef<string[]>([])
  useEffect(() => {
    if (!isTauri() || isPopupWindow()) return
    const paths = recent.current.filter(path => path !== pathname).slice(0, 2)
    recent.current = [pathname, ...paths]
    if (!paths.length) return
    let cancelled = false
    let idle: number | undefined
    const timer = window.setTimeout(() => {
      const run = async () => {
        if (
          cancelled ||
          document.visibilityState !== 'visible' ||
          !(await isMainWindow())
        )
          return
        for (const path of paths) {
          if (cancelled || document.visibilityState !== 'visible') return
          await prefetchModuleForPath(path)
          if (!cancelled)
            await prefetchNavigationData(
              path,
              client,
              getWorkspaceSessionSnapshot(),
            )
        }
      }
      if ('requestIdleCallback' in window)
        idle = window.requestIdleCallback(() => void run())
      else void run()
    }, 3000)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
      if (idle !== undefined) window.cancelIdleCallback(idle)
    }
  }, [pathname, client])
}
